import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Link,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Refresh as RefreshIcon, 
  Info as InfoIcon // Import Info icon for status
} from '@mui/icons-material';
import api from '../../utils/axios';

const AdminServers = () => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newServerName, setNewServerName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null); // Store ID of server being deleted

  // State for status modal
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [currentServerStatus, setCurrentServerStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState('');
  const [viewingServerName, setViewingServerName] = useState('');

  const fetchServers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/servers');
      setServers(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch servers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServers();
  }, []);

  const handleOpenCreateDialog = () => {
    setNewServerName('');
    setOpenCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };

  const handleCreateServer = async () => {
    if (!newServerName.trim()) {
      setError('Server name cannot be empty.');
      return;
    }
    try {
      setCreateLoading(true);
      setError('');
      await api.post('/admin/servers', { name: newServerName });
      handleCloseCreateDialog();
      await fetchServers(); // Refresh the list
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create server.');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteServer = async (serverId) => {
    if (!window.confirm('Are you sure you want to delete this server? This action is irreversible.')) {
      return;
    }
    try {
      setDeleteLoading(serverId);
      setError('');
      await api.delete(`/admin/servers/${serverId}`);
      await fetchServers(); // Refresh the list
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete server.');
    } finally {
      setDeleteLoading(null);
    }
  };

  // Function to fetch and show server status
  const handleViewStatus = async (server) => {
    if (!server || !server._id) return;
    setViewingServerName(server.name);
    setStatusLoading(true);
    setStatusError('');
    setCurrentServerStatus(null);
    setOpenStatusDialog(true);

    try {
      const response = await api.get(`/admin/servers/${server._id}/status`);
      setCurrentServerStatus(response.data.source);
    } catch (err) {
      setStatusError(err.response?.data?.error || err.response?.data?.details || 'Failed to fetch server status.');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleCloseStatusDialog = () => {
    setOpenStatusDialog(false);
    setCurrentServerStatus(null);
    setStatusError('');
    setViewingServerName('');
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Admin: Manage Icecast Servers
          </Typography>
          <Box>
             <IconButton onClick={fetchServers} disabled={loading} color="primary">
              <RefreshIcon />
            </IconButton>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={handleOpenCreateDialog}
            >
              Create Server
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper elevation={3}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {servers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No servers found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    servers.map((server) => (
                      <TableRow key={server._id}>
                        <TableCell>{server.name}</TableCell>
                        <TableCell>
                          {server.ipAddress ? (
                            <Link 
                              href={`http://${server.ipAddress}:8000/admin/stats.xsl`}
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              {server.ipAddress}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>{server.region}</TableCell>
                        <TableCell>{server.size}</TableCell>
                        <TableCell>{server.status}</TableCell>
                        <TableCell>{new Date(server.createdAt).toLocaleString()}</TableCell>
                        <TableCell align="right">
                          {/* Status Button - Only show if server is active */}
                          {server.status === 'active' && (
                            <IconButton 
                              color="info" 
                              onClick={() => handleViewStatus(server)}
                              disabled={statusLoading} // Disable while any status is loading?
                              size="small"
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          )}
                          {/* Delete Button */}
                          <IconButton 
                            color="error" 
                            onClick={() => handleDeleteServer(server._id)}
                            disabled={deleteLoading === server._id}
                            size="small"
                          >
                            {deleteLoading === server._id ? <CircularProgress size={20} /> : <DeleteIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {/* Create Server Dialog */}
      <Dialog open={openCreateDialog} onClose={handleCloseCreateDialog}>
        <DialogTitle>Create New Icecast Server</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter a friendly name for the new Icecast server. A new DigitalOcean droplet will be provisioned.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Server Name"
            type="text"
            fullWidth
            variant="standard"
            value={newServerName}
            onChange={(e) => setNewServerName(e.target.value)}
            disabled={createLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog} disabled={createLoading}>Cancel</Button>
          <Button onClick={handleCreateServer} variant="contained" disabled={createLoading}>
            {createLoading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Dialog */}
      <Dialog open={openStatusDialog} onClose={handleCloseStatusDialog} maxWidth="md" fullWidth>
        <DialogTitle>Icecast Status: {viewingServerName}</DialogTitle>
        <DialogContent dividers>
          {statusLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : statusError ? (
            <Alert severity="error">{statusError}</Alert>
          ) : (
            <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              <Typography variant="h6" gutterBottom>Mount Point(s)</Typography>
              {currentServerStatus ? (
                () => {
                  // Ensure currentServerStatus is an array
                  const sources = Array.isArray(currentServerStatus) 
                                    ? currentServerStatus 
                                    : [currentServerStatus]; // Wrap single object in array

                  if (sources.length === 0 || !sources[0]) { // Check if array is empty or first element is null/falsy
                     return <Typography>No active source/mount point found.</Typography>;
                  }
                  
                  // Helper to extract mount name from URL
                  const getMountName = (url) => {
                    try {
                      const path = new URL(url).pathname;
                      return path || 'N/A';
                    } catch (e) {
                      return 'N/A';
                    }
                  };

                  return (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Mount</TableCell>
                            <TableCell>Listeners</TableCell>
                            <TableCell>Peak</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Audio Info</TableCell>
                            <TableCell>URL</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sources.map((source, index) => (
                            <TableRow key={index}> {/* Use index if no unique ID available */}
                              <TableCell>{getMountName(source.listenurl)}</TableCell>
                              <TableCell>{source.listeners ?? 'N/A'}</TableCell>
                              <TableCell>{source.listener_peak ?? 'N/A'}</TableCell>
                              <TableCell>{source.server_name ?? 'N/A'}</TableCell>
                              <TableCell>{source.server_description ?? 'N/A'}</TableCell>
                              <TableCell>{source.audio_info ?? 'N/A'}</TableCell>
                              <TableCell>
                                <Link href={source.listenurl} target="_blank" rel="noopener noreferrer">
                                  Link
                                </Link>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  );
                }
              )() : (
                <Typography>No active source/mount point found.</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog}>Close</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default AdminServers; 